import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Stethoscope, 
  ShoppingCart, 
  GraduationCap, 
  Rocket, 
  Settings,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import Newsletter from '../components/Newsletter';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const ServiceSection = ({ id, icon: Icon, title, description, features, image, reverse }) => (
  <motion.div
    id={id}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${reverse ? 'lg:grid-flow-dense' : ''}`}
    data-testid={`service-${id}`}
  >
    <div className={reverse ? 'lg:col-start-2' : ''}>
      <div className="w-14 h-14 rounded-2xl bg-cyan-50 flex items-center justify-center text-cyan-600 mb-6">
        <Icon className="w-7 h-7" />
      </div>
      <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
        {title}
      </h2>
      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        {description}
      </p>
      
      <motion.ul
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="space-y-4 mb-8"
      >
        {features.map((feature, i) => (
          <motion.li key={i} variants={itemVariants} className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-cyan-500 mt-0.5 flex-shrink-0" />
            <span className="text-slate-600">{feature}</span>
          </motion.li>
        ))}
      </motion.ul>

      <Link to="/contact">
        <Button 
          className="btn-gradient text-white rounded-full px-6 py-3 font-medium shadow-lg shadow-cyan-500/20"
          data-testid={`service-cta-${id}`}
        >
          Learn More
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </Link>
    </div>

    <div className={reverse ? 'lg:col-start-1' : ''}>
      <div className="relative rounded-3xl overflow-hidden shadow-xl">
        <img
          src={image}
          alt={title}
          className="w-full h-[400px] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent"></div>
      </div>
    </div>
  </motion.div>
);

export default function Services() {
  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-sm font-medium tracking-wider uppercase text-cyan-600 mb-4 block">
              Our Services
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6">
              Comprehensive Healthcare Technology Solutions
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              From consultation to deployment and ongoing management, we provide end-to-end services to modernize your healthcare infrastructure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20" data-testid="services-detail-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-32">
            <ServiceSection
              id="consultation"
              icon={Stethoscope}
              title="Technical Consultation"
              description="Our team of healthcare IT experts provides comprehensive consultation services to help you navigate the complex landscape of medical technology. We analyze your current infrastructure, identify gaps, and develop strategic roadmaps for digital transformation."
              features={[
                'Infrastructure assessment and audit',
                'Digital transformation strategy',
                'Technology stack recommendations',
                'Compliance and security evaluation',
                'Budget planning and ROI analysis',
              ]}
              image="https://images.unsplash.com/photo-1706777268519-f7aa1898b823?auto=format&fit=crop&q=80&w=800"
              reverse={false}
            />

            <ServiceSection
              id="procurement"
              icon={ShoppingCart}
              title="Procurement"
              description="We streamline the procurement process for healthcare technology, leveraging our industry partnerships to secure the best solutions at competitive prices. Our procurement specialists ensure you get exactly what you need."
              features={[
                'Vendor evaluation and selection',
                'Contract negotiation',
                'Equipment specification analysis',
                'Cost optimization strategies',
                'Supply chain management',
              ]}
              image="https://images.unsplash.com/photo-1606206873764-fd15e242df52?auto=format&fit=crop&q=80&w=800"
              reverse={true}
            />

            <ServiceSection
              id="training"
              icon={GraduationCap}
              title="Training"
              description="Empower your healthcare staff with the knowledge and skills needed to effectively utilize new technologies. Our training programs are customized to your organization's specific needs and learning objectives."
              features={[
                'Customized training curricula',
                'Hands-on workshops and simulations',
                'Online learning platforms',
                'Certification programs',
                'Ongoing education support',
              ]}
              image="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&q=80&w=800"
              reverse={false}
            />

            <ServiceSection
              id="deployment"
              icon={Rocket}
              title="Deployment"
              description="Our deployment teams ensure smooth implementation of healthcare technology solutions with minimal disruption to your operations. We handle everything from planning to go-live and beyond."
              features={[
                'Project planning and management',
                'System integration',
                'Data migration and validation',
                'Testing and quality assurance',
                'Go-live support and stabilization',
              ]}
              image="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=800"
              reverse={true}
            />

            <ServiceSection
              id="management"
              icon={Settings}
              title="Management"
              description="Keep your healthcare IT infrastructure running at peak performance with our comprehensive management services. Our team provides round-the-clock monitoring, maintenance, and support."
              features={[
                '24/7 monitoring and support',
                'Proactive maintenance',
                'Security updates and patches',
                'Performance optimization',
                'Disaster recovery planning',
              ]}
              image="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=800"
              reverse={false}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Not Sure Which Service You Need?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
              Our experts are here to help you identify the best solutions for your healthcare organization's unique challenges.
            </p>
            <Link to="/contact">
              <Button 
                className="btn-gradient text-white rounded-full px-8 py-4 text-base font-semibold shadow-lg shadow-cyan-500/20"
                data-testid="services-contact-cta"
              >
                Schedule a Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
}
